import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Printer, Warning, CloudArrowUp } from '@phosphor-icons/react';
import API from '@/lib/api';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { PrintMenuControl } from '@/components/PrintMenuControl';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';

/* ════════════════════════════════════════════════════════════════════
   RemotePrintButton — knop met dropdown om een document remote te
   printen op een van de geregistreerde lokale printers (BillFac Print
   Agent) of via PrintNode (cloud-printdienst).

   Props:
     documentId : str  (de id van de factuur/leverbon/etc.)
     documentType: 'factuur' | 'leverbon' | ...
     bestandsnaam: str
     testid     : str (extra)
   ════════════════════════════════════════════════════════════════════ */
export default function RemotePrintButton({ documentId, documentType = 'pdf', bestandsnaam = 'document.pdf', testid = '', label = '', menuItem = false, compactIcon = false }) {
  const [open, setOpen] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [pnPrinters, setPnPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sending, setSending] = useState(false);
  const [listErrors, setListErrors] = useState({});
  const request = useRef(null);
  const mounted = useRef(true);
  const ref = useRef(null);
  const pickerRef = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; request.current?.abort(); };
  }, []);

  // Laad printers (agent + PrintNode) bij eerste open
  const loadPrinters = async (force = false) => {
    if ((loaded && !force) || loading) return;
    setLoading(true);
    setListErrors({});
    const controller = new AbortController();
    request.current = controller;
    try {
      const [agentRes, pnRes] = await Promise.allSettled([
        API.get('/print-printers', { noCache: force, signal: controller.signal }),
        API.get('/printnode/printers', { noCache: force, signal: controller.signal }),
      ]);
      if (!mounted.current || controller.signal.aborted) return;
      const errors = {};
      if (agentRes.status === 'rejected') errors.agent = 'Lokale printers konden niet worden geladen.';
      if (pnRes.status === 'rejected') {
        const detail = pnRes.reason?.response?.data?.detail;
        errors.printnode = /VAT information is inconsistent or incomplete/i.test(typeof detail === 'string' ? detail : '')
          ? 'PrintNode is geblokkeerd: controleer de btw- en facturatiegegevens in je PrintNode-account bij Payment Info.'
          : 'PrintNode-printers konden niet worden geladen.';
      }
      setPrinters(agentRes.status === 'fulfilled' ? agentRes.value.data?.printers || [] : []);
      setPnPrinters(pnRes.status === 'fulfilled' ? pnRes.value.data?.printers || [] : []);
      setListErrors(errors);
      setLoaded(!Object.keys(errors).length);
    } finally {
      if (mounted.current && !controller.signal.aborted) setLoading(false);
    }
  };

  // Klik buiten dropdown → sluiten
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !pickerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) loadPrinters();
    setOpen(!open);
  };

  // Print via BillFac Print Agent (queue-model)
  const sendJob = async (printer) => {
    if (sending) return;
    if (menuItem) ref.current?.querySelector(`[data-testid="${testid || `remote-print-${documentId}`}"]`)?.focus();
    else setOpen(false);
    setSending(true);
    try {
      const pdfUrlFull = `/documenten/${documentId}/pdf`;
      const r = await API.post('/print-jobs', {
        printer_id: printer.id,
        document_id: documentId,
        document_type: documentType,
        pdf_url: pdfUrlFull,
        bestandsnaam,
        copies: 1,
      });
      toast.success(`📄 Naar wachtrij voor "${printer.alias || printer.naam}"`, {
        description: `Job ${r.data.id} · uw lokale Print Agent zal binnen enkele seconden afdrukken.`,
        duration: 4500,
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error('Print mislukt: ' + msg);
    } finally {
      setSending(false);
    }
  };

  // Print via PrintNode (directe cloud-verzending)
  const sendPrintNode = async (printer) => {
    if (sending) return;
    // Keep the nested menu collection mounted and focus its stable trigger before disabling targets.
    if (menuItem) ref.current?.querySelector(`[data-testid="${testid || `remote-print-${documentId}`}"]`)?.focus();
    setSending(true);
    try {
      const r = await API.post('/printnode/print', {
        document_id: documentId,
        printer_id: printer.id,
        copies: 1,
      });
      if (!menuItem) setOpen(false);
      toast.success(`🖨️ Verzonden naar "${printer.naam}" via PrintNode`, {
        description: `PrintJob #${r.data.printjob_id} · ${r.data.bestandsnaam}`,
        duration: 4500,
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error('PrintNode print mislukt: ' + msg);
    } finally {
      setSending(false);
    }
  };

  const onlinePrinters = printers.filter(p => p.status === 'online');
  const pnOnline = pnPrinters.filter(p => p.online);
  const hasAgent = printers.length > 0;
  const hasPn = pnPrinters.length > 0;
  const hasAny = hasAgent || hasPn;

  const trigger = <button
        type="button"
        className={compactIcon ? 'invoice-action-icon' : menuItem ? 'invoice-overflow-action' : label ? 'btn btn-secondary btn-sm inline-flex items-center gap-1' : 'btn btn-ghost btn-sm'}
        onClick={toggle}
        title="Print remote op uw kantoorprinter (PrintNode / Print Agent)"
        aria-label="Afdrukken"
        aria-expanded={open}
        data-testid={testid || `remote-print-${documentId}`}
      >
        <Printer size={14} weight="bold" className={label ? '' : 'text-[#002FA7]'} />
        {label && <>{!menuItem && <CloudArrowUp size={13} weight="bold" />} {label}</>}
      </button>;
  const picker = open && (
        <div ref={pickerRef} className={menuItem || compactIcon ? 'bg-white border border-gray-200 w-full min-w-0' : 'absolute right-0 top-full mt-1 z-50 bg-white border-2 border-[#002FA7] shadow-xl min-w-[280px]'} onClick={e => e.stopPropagation()} data-testid="remote-print-dropdown">
          <div className="px-3 py-2 border-b border-gray-200 bg-[#EFF6FF] text-xs font-bold text-[#1E40AF] flex items-center gap-2">
            <Printer size={14} weight="duotone" />
            Print remote op…
          </div>
          {loading && (
            <div className="px-3 py-3 text-xs text-gray-500">Laden…</div>
          )}
          {!loading && Object.keys(listErrors).length > 0 && <div className="px-3 py-2 text-xs text-red-800 bg-red-50 whitespace-normal" role="alert" data-testid={`print-list-error-${documentId}`}>
            {Object.entries(listErrors).map(([service, message]) => <p key={service} className="mb-1" data-testid={`print-error-${service}-${documentId}`}>{message}</p>)}
            <PrintMenuControl menuItem={menuItem}><button type="button" onClick={e => { e.stopPropagation(); loadPrinters(true); }} className="underline py-1" data-testid={`print-list-retry-${documentId}`}>Opnieuw proberen</button></PrintMenuControl>
          </div>}
          {!loading && !hasAny && !Object.keys(listErrors).length && (
            <div className="px-3 py-3 text-xs text-gray-600">
              <div className="flex items-start gap-2">
                <Warning size={16} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Geen printers gevonden</div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    Koppel <strong>PrintNode</strong> of installeer de BillFac Print Agent via{' '}
                    <a href="/instellingen?tab=integraties" className="text-[#002FA7] underline">Instellingen → Integraties</a>.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PrintNode printers ── */}
          {!loading && hasPn && (
            <>
              <div className="px-3 py-1.5 bg-[#F0FDF4] border-b border-gray-100 text-[10px] font-bold uppercase tracking-wide text-[#166534] flex items-center gap-1.5">
                <CloudArrowUp size={12} weight="bold" /> PrintNode
              </div>
              {pnOnline.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-[#92400E] bg-[#FEF3C7]">
                  ⚠️ Geen PrintNode-printers online. Start de PrintNode Client op uw PC.
                </div>
              )}
              <div className="max-h-56 overflow-y-auto">
                {pnPrinters.map(p => (
                  <PrintMenuControl key={`pn-${p.id}`} menuItem={menuItem}>
                  <button
                    key={`pn-${p.id}`}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[#F0FDF4] border-b border-gray-100 flex items-center gap-2 ${!p.online ? 'opacity-50 cursor-not-allowed' : ''} ${sending ? 'opacity-60' : ''}`}
                    onClick={() => p.online && sendPrintNode(p)}
                    disabled={!p.online || sending}
                    data-testid={`printnode-target-${p.id}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${p.online ? 'bg-[#10B981]' : 'bg-gray-400'}`}></span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">
                        {p.naam}
                        {p.is_default && <span className="ml-1 text-[9px] uppercase px-1 py-0.5 bg-[#DCFCE7] text-[#166534]">default</span>}
                      </div>
                      <div className="text-[10px] text-gray-400 truncate">{p.computer}</div>
                    </div>
                    <span className="text-[9px] uppercase text-gray-500">{sending ? '…' : 'cloud'}</span>
                  </button>
                  </PrintMenuControl>
                ))}
              </div>
            </>
          )}

          {/* ── BillFac Print Agent printers ── */}
          {!loading && hasAgent && (
            <>
              {hasPn && (
                <div className="px-3 py-1.5 bg-[#EFF6FF] border-b border-gray-100 text-[10px] font-bold uppercase tracking-wide text-[#1E40AF] flex items-center gap-1.5">
                  <Printer size={12} weight="bold" /> BillFac Print Agent
                </div>
              )}
              {onlinePrinters.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-[#92400E] bg-[#FEF3C7]">
                  ⚠️ Geen agent-printers online. Start de Print Agent op uw PC.
                </div>
              )}
              <div className="max-h-56 overflow-y-auto">
                {printers.map(p => (
                  <PrintMenuControl key={p.id} menuItem={menuItem}>
                  <button
                    key={p.id}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[#EFF6FF] border-b border-gray-100 flex items-center gap-2 ${p.status !== 'online' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => p.status === 'online' && sendJob(p)}
                    disabled={p.status !== 'online' || sending}
                    data-testid={`remote-print-target-${p.id}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${p.status === 'online' ? 'bg-[#10B981]' : 'bg-gray-400'}`}></span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">
                        {p.alias || p.naam}
                        {p.is_default && <span className="ml-1 text-[9px] uppercase px-1 py-0.5 bg-[#DBEAFE] text-[#1E40AF]">default</span>}
                      </div>
                      {p.alias && <div className="text-[10px] text-gray-400 truncate">{p.naam}</div>}
                    </div>
                    <span className="text-[9px] uppercase text-gray-500">{p.type}</span>
                  </button>
                  </PrintMenuControl>
                ))}
              </div>
            </>
          )}

          <div className="px-3 py-1.5 border-t border-gray-200 bg-gray-50 text-[10px] text-gray-500 flex items-center justify-between">
            <span>{pnPrinters.length + printers.length} printer{(pnPrinters.length + printers.length) !== 1 ? 's' : ''} · {pnOnline.length + onlinePrinters.length} online</span>
            <PrintMenuControl menuItem={menuItem}><a href="/instellingen?tab=integraties" className="text-[#002FA7] hover:underline" data-testid={`print-settings-${documentId}`}>⚙ Beheren</a></PrintMenuControl>
          </div>
        </div>
      );
  return (
    <Popover open={compactIcon && open} onOpenChange={value => { if (compactIcon) setOpen(value); }}>
      <div className={menuItem ? 'invoice-print-menu' : 'relative inline-block'} ref={ref}>
        {compactIcon ? <PopoverAnchor asChild>{trigger}</PopoverAnchor> : menuItem ? <DropdownMenuItem asChild onSelect={e => e.preventDefault()}>{trigger}</DropdownMenuItem> : trigger}
        {!compactIcon && picker}
      </div>
      {compactIcon && <PopoverContent align="end" collisionPadding={8} className="!p-0 w-72 max-w-[calc(100vw-16px)] bg-white overflow-y-auto z-[100]" style={{ maxHeight: 'min(22rem, var(--radix-popover-content-available-height))' }} aria-label="Printer kiezen" data-testid={`print-popover-${documentId}`}>{picker}</PopoverContent>}
    </Popover>
  );
}
